import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TablePagination,
  TextField,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Chip,
  useTheme,
  useMediaQuery,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  OutlinedInput
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Support as SupportIcon
} from '@mui/icons-material';
import { supportContractService } from '../services/supportContractService';
import { companyService } from '../services/companyService';

const SupportContracts = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const [contracts, setContracts] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [filteredContracts, setFilteredContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [contractToDelete, setContractToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  
  // Sorting state
  const [orderBy, setOrderBy] = useState('companyId');
  const [order, setOrder] = useState('asc');
  
  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  
  // Filter state
  const [searchFilters, setSearchFilters] = useState({
    contractName: '',
    company: '',
    status: ['Active'], // Default to Active only
    createdDate: ''
  });

  const loadCompanies = async () => {
    try {
      const companiesData = await companyService.getCompanies();
      setCompanies(companiesData);
    } catch (err) {
      console.error('Error loading companies:', err);
    }
  };

  const getCompanyName = useCallback((companyId) => {
    if (!companyId) return '';
    const company = companies.find(c => c.companyId === companyId);
    return company ? company.companyName : '';
  }, [companies]);

  const loadContracts = async (showLoadingIndicator = true) => {
    if (showLoadingIndicator) {
      setLoading(true);
    } else {
      setRefreshing(true);
    }
    setError('');
    
    try {
      supportContractService.validateConfiguration();
      console.log('🔄 [SupportContracts] Starting to load support contracts...');
      const contractsData = await supportContractService.getSupportContracts();
      console.log('📊 [SupportContracts] Raw data received:', contractsData);
      console.log('📊 [SupportContracts] Number of contracts:', contractsData.length);
      
      setContracts(contractsData);
      console.log('✅ [SupportContracts] State updated with new data');
      console.log('Support contracts loaded successfully:', contractsData.length);
    } catch (err) {
      console.error('❌ [SupportContracts] Error loading support contracts:', err);
      if (err.message.includes('Missing required environment variables')) {
        setError(`Configuration Error: ${err.message}. Please check your .env file.`);
      } else if (err.message.includes('Failed to fetch support contracts: 404')) {
        // Try to initialize the file for first-time setup
        try {
          const initializedContracts = await supportContractService.initializeSupportContractsFile();
          setContracts(initializedContracts);
        } catch (initError) {
          setError(`Failed to initialize support contracts: ${initError.message}`);
        }
      } else if (err.message.includes('403') || err.message.includes('Server failed to authenticate')) {
        setError('Temporary authentication issue. Please try refreshing again in a moment.');
        console.warn('Support contracts page: Authentication error - user should retry');
      } else {
        setError(`Failed to load support contracts: ${err.message}`);
      }
    } finally {
      if (showLoadingIndicator) {
        setLoading(false);
      } else {
        setRefreshing(false);
      }
    }
  };

  const applyFiltersAndSort = useCallback(() => {
    let filtered = [...contracts];

    // Apply filters
    Object.keys(searchFilters).forEach(key => {
      if (searchFilters[key]) {
        filtered = filtered.filter(contract => {
          if (key === 'company') {
            // Filter by company name
            const companyName = getCompanyName(contract.companyId).toLowerCase();
            return companyName.includes(searchFilters[key].toLowerCase());
          } else if (key === 'status') {
            // Handle status array filtering
            const statusArray = searchFilters[key];
            if (statusArray.length === 0) return true; // Show all if no status selected
            return statusArray.includes(contract.status);
          } else {
            const value = contract[key]?.toString().toLowerCase() || '';
            return value.includes(searchFilters[key].toLowerCase());
          }
        });
      }
    });

    // Apply sorting
    filtered.sort((a, b) => {
      let aValue, bValue;
      
      if (orderBy === 'company') {
        aValue = getCompanyName(a.companyId).toLowerCase();
        bValue = getCompanyName(b.companyId).toLowerCase();
      } else if (orderBy === 'createdDate') {
        aValue = new Date(a[orderBy]);
        bValue = new Date(b[orderBy]);
      } else if (orderBy === 'maxTicketsPerMonth') {
        aValue = parseInt(a[orderBy]) || 0;
        bValue = parseInt(b[orderBy]) || 0;
      } else {
        aValue = a[orderBy]?.toString().toLowerCase() || '';
        bValue = b[orderBy]?.toString().toLowerCase() || '';
      }

      if (order === 'asc') {
        return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
      } else {
        return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
      }
    });

    setFilteredContracts(filtered);
  }, [contracts, searchFilters, order, orderBy, getCompanyName]);

  useEffect(() => {
    loadContracts();
    loadCompanies();
  }, []);

  // Auto-refresh functionality
  useEffect(() => {
    // Refresh data when window regains focus (user switches back to tab)
    const handleFocus = () => {
      console.log('Window focused, refreshing support contracts data...');
      loadContracts(true);
      loadCompanies();
    };

    // Refresh data every 2 minutes when tab is active
    const refreshInterval = setInterval(() => {
      if (!document.hidden) {
        console.log('Auto-refreshing support contracts data...');
        loadContracts(false);
        loadCompanies();
      }
    }, 2 * 60 * 1000); // 2 minutes

    window.addEventListener('focus', handleFocus);

    return () => {
      window.removeEventListener('focus', handleFocus);
      clearInterval(refreshInterval);
    };
  }, []);

  useEffect(() => {
    applyFiltersAndSort();
  }, [contracts, searchFilters, order, orderBy, applyFiltersAndSort]);

  const handleSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleFilterChange = (field) => (event) => {
    setSearchFilters(prev => ({
      ...prev,
      [field]: event.target.value
    }));
    setPage(0); // Reset to first page when filtering
  };

  const handleStatusFilterChange = (event) => {
    const value = event.target.value;
    let newStatus;
    
    if (value.length === 0) {
      // If nothing is selected, default to Active
      newStatus = ['Active'];
    } else {
      newStatus = typeof value === 'string' ? value.split(',') : value;
    }
    
    setSearchFilters(prev => ({
      ...prev,
      status: newStatus
    }));
    setPage(0); // Reset to first page when filtering
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleCreateContract = () => {
    navigate('/support-contract/new');
  };

  const handleEditContract = (contractId) => {
    navigate(`/support-contract/${contractId}`);
  };

  const handleDeleteClick = (contract) => {
    setContractToDelete(contract);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!contractToDelete) return;

    setDeleting(true);
    try {
      await supportContractService.deleteSupportContract(contractToDelete.contractId);
      await loadContracts();
      setDeleteDialogOpen(false);
      setContractToDelete(null);
    } catch (err) {
      console.error('Error deleting support contract:', err);
      setError(`Failed to delete support contract: ${err.message}`);
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setContractToDelete(null);
  };

  const formatDate = (dateString) => {
    // Parse the date string as local date to avoid timezone issues
    const date = new Date(dateString);
    return date.toLocaleDateString();
  };

  const getStatusChip = (status) => {
    return (
      <Chip
        label={status}
        color={status === 'Active' ? 'success' : 'default'}
        size="small"
        variant="outlined"
      />
    );
  };

  // Calculate paginated contracts
  const paginatedContracts = filteredContracts.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h4" sx={{ display: 'flex', alignItems: 'center' }}>
          <SupportIcon sx={{ mr: 2 }} />
          Support Contracts
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <IconButton 
            onClick={() => loadContracts()} 
            title="Refresh" 
            disabled={refreshing}
          >
            <RefreshIcon />
          </IconButton>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleCreateContract}
          >
            Create Support Contract
          </Button>
        </Box>
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Contracts Table */}
      <Paper>
        {/* Search Filters - Aligned with table columns */}
        <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
          {/* Mobile filters */}
          {isMobile && (
            <Stack spacing={2}>
              <TextField
                size="small"
                label="Contract Name"
                value={searchFilters.contractName}
                onChange={handleFilterChange('contractName')}
                fullWidth
              />
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  multiple
                  value={searchFilters.status}
                  onChange={handleStatusFilterChange}
                  input={<OutlinedInput label="Status" />}
                  renderValue={(selected) => {
                    if (selected.length === 0) return 'None';
                    return selected.join(', ');
                  }}
                >
                  <MenuItem value="Active">
                    <Checkbox checked={searchFilters.status.indexOf('Active') > -1} />
                    <ListItemText primary="Active" />
                  </MenuItem>
                  <MenuItem value="Closed">
                    <Checkbox checked={searchFilters.status.indexOf('Closed') > -1} />
                    <ListItemText primary="Closed" />
                  </MenuItem>
                </Select>
              </FormControl>
            </Stack>
          )}
          
          {filteredContracts.length !== contracts.length && (
            <Box sx={{ mt: 2 }}>
              <Chip 
                label={`Showing ${filteredContracts.length} of ${contracts.length} contracts`}
                size="small"
                color="primary"
                variant="outlined"
              />
            </Box>
          )}
        </Box>

        <TableContainer>
          <Table>
            <TableHead>
              {/* Search filters row - only show on desktop */}
              {!isMobile && (
                <TableRow>
                  <TableCell sx={{ borderBottom: 'none', pb: 1 }}>
                    <TextField
                      size="small"
                      placeholder="Search Contract Name"
                      value={searchFilters.contractName}
                      onChange={handleFilterChange('contractName')}
                      fullWidth
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell sx={{ borderBottom: 'none', pb: 1 }}>
                    <TextField
                      size="small"
                      placeholder="Search Company"
                      value={searchFilters.company}
                      onChange={handleFilterChange('company')}
                      fullWidth
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell sx={{ borderBottom: 'none', pb: 1 }} align="center">
                    <FormControl fullWidth size="small">
                      <InputLabel>Status</InputLabel>
                      <Select
                        multiple
                        value={searchFilters.status}
                        onChange={handleStatusFilterChange}
                        input={<OutlinedInput label="Status" />}
                        renderValue={(selected) => {
                          if (selected.length === 0) return 'None';
                          return selected.join(', ');
                        }}
                      >
                        <MenuItem value="Active">
                          <Checkbox checked={searchFilters.status.indexOf('Active') > -1} />
                          <ListItemText primary="Active" />
                        </MenuItem>
                        <MenuItem value="Closed">
                          <Checkbox checked={searchFilters.status.indexOf('Closed') > -1} />
                          <ListItemText primary="Closed" />
                        </MenuItem>
                      </Select>
                    </FormControl>
                  </TableCell>
                  <TableCell sx={{ borderBottom: 'none', pb: 1 }}>
                    <TextField
                      size="small"
                      placeholder="Search Created Date"
                      value={searchFilters.createdDate}
                      onChange={handleFilterChange('createdDate')}
                      fullWidth
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell sx={{ borderBottom: 'none', pb: 1 }} align="center" width={120}>
                    {/* Actions column - no filter */}
                  </TableCell>
                </TableRow>
              )}
              
              {/* Column headers row */}
              <TableRow>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'contractName'}
                    direction={orderBy === 'contractName' ? order : 'asc'}
                    onClick={() => handleSort('contractName')}
                  >
                    Contract Name
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'company'}
                    direction={orderBy === 'company' ? order : 'asc'}
                    onClick={() => handleSort('company')}
                  >
                    Company
                  </TableSortLabel>
                </TableCell>
                <TableCell align="center">
                  <TableSortLabel
                    active={orderBy === 'status'}
                    direction={orderBy === 'status' ? order : 'asc'}
                    onClick={() => handleSort('status')}
                  >
                    Status
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'createdDate'}
                    direction={orderBy === 'createdDate' ? order : 'asc'}
                    onClick={() => handleSort('createdDate')}
                  >
                    Created Date
                  </TableSortLabel>
                </TableCell>
                <TableCell align="center" width={120}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {refreshing ? (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 2 }}>
                      <CircularProgress size={20} sx={{ mr: 1 }} />
                      <Typography variant="body2">Refreshing...</Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : paginatedContracts.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    No support contracts found
                  </TableCell>
                </TableRow>
              ) : (
                paginatedContracts.map((contract) => (
                  <TableRow key={contract.contractId} hover>
                    <TableCell>{contract.contractName}</TableCell>
                    <TableCell>{getCompanyName(contract.companyId)}</TableCell>
                    <TableCell align="center">{getStatusChip(contract.status)}</TableCell>
                    <TableCell>{formatDate(contract.createdDate)}</TableCell>
                    <TableCell align="center">
                      <IconButton
                        size="small"
                        onClick={() => handleEditContract(contract.contractId)}
                        title="Edit"
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteClick(contract)}
                        title="Delete"
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        
        {/* Pagination */}
        <TablePagination
          component="div"
          count={filteredContracts.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 25, 50, 100]}
        />
      </Paper>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={handleDeleteCancel}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this support contract?
          </Typography>
          {contractToDelete && (
            <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
              <Typography variant="body2">
                <strong>Contract Name:</strong> {contractToDelete.contractName}
              </Typography>
              <Typography variant="body2">
                <strong>Company:</strong> {getCompanyName(contractToDelete.companyId)}
              </Typography>
              <Typography variant="body2">
                <strong>Status:</strong> {contractToDelete.status}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} disabled={deleting}>
            Cancel
          </Button>
          <Button 
            onClick={handleDeleteConfirm} 
            color="error" 
            variant="contained"
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={16} /> : <DeleteIcon />}
          >
            {deleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SupportContracts;